/* ⚠️ GENERATED from app/services/profileStore.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/profileStore.ts — turning "show this, hide that" into writes.
//
// 🟥 THIS MODULE TOUCHES NO DATABASE, AND THAT IS DELIBERATE. It works out
//    WHICH writes a save needs and hands them back; the two clients perform
//    them with their own imports. The alternative is this logic existing twice,
//    once per client, which is how the phone ends up hiding a field the browser
//    still publishes. Same reason `aboutStatus.buildAboutWrite` is shaped this
//    way, and the twin of this file is generated from it.
//
// 🟥 A MOVE IS TWO WRITES AND MUST BE ONE OPERATION. Hiding an answer removes
//    it from the public document AND adds it to the owner-only one. Done as two
//    separate saves, an interruption between them leaves the answer in both
//    places (still public, owner told otherwise) or in neither (silently lost).
//    The plan below is written for a batch, and the callers use one.
//
// 🟢 AND THE OWNER-ONLY MAP IS WRITTEN WHOLE, not patched. It is one small
//    document that only its owner reads, and replacing it outright means a key
//    that was shown again cannot linger in it as a second copy.

import { FIELD_KEYS, homeOf, pageFieldOrder } from './profileFields';

/** What the owner's editor started from. */

/**
 * The writes a save needs.
 *
 * `set` and `clear` are dotted paths on the PUBLIC document; `hidden` is the
 * whole contents of the owner-only document. `clear` is separate from `set`
 * because a client turns it into its own delete sentinel, and a plan that
 * carried the sentinel would be a plan that imports a database.
 */

const norm = m => {
  const out = {};
  if (!m || typeof m !== 'object') return out;
  for (const k of Object.keys(m)) {
    const v = m[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  }
  return out;
};
const pathOf = (key, kind) => {
  const home = homeOf(key, kind);
  if (home === 'root') return key;
  if (home === 'extra') return `extra.${key}`;
  return `profileFields.${key}`;
};

/**
 * Everything the owner is editing, in one list, whichever of the three homes
 * each answer currently sits in.
 */
export function editableFields(snap, kind) {
  const fields = norm(snap.fields);
  const root = kind === 'page' ? norm(snap.root) : {};
  const extra = kind === 'page' ? norm(snap.extra) : {};
  const hidden = norm(snap.hidden);
  const pub = {
    ...extra,
    ...root,
    ...fields
  };
  const order = kind === 'page' ? pageFieldOrder([...Object.keys(extra), ...Object.keys(hidden)]) : [...FIELD_KEYS, ...Object.keys(hidden).filter(k => !FIELD_KEYS.includes(k))];
  const seen = new Set();
  const out = [];
  for (const key of order) {
    if (seen.has(key)) continue;
    seen.add(key);
    /* 🟥 PUBLIC WINS A KEY THAT IS IN BOTH. It is already readable, so calling
       it hidden would be the one wrong answer that matters. */
    if (pub[key]) out.push({
      key,
      value: pub[key],
      visibility: 'public'
    });else if (hidden[key]) out.push({
      key,
      value: hidden[key],
      visibility: 'hidden'
    });
  }
  return out;
}

/**
 * The writes that take a profile from `snap` to `next`.
 *
 * 🟥 AN ANSWER THAT MOVED MUST BE CLEARED FROM WHERE IT WAS. This is the whole
 *    job: writing the value into the owner-only document and forgetting to
 *    remove the public copy leaves it exactly as readable as it was, on a
 *    screen that now says it is private.
 */
export function planSave(snap, next, kind) {
  const fields = norm(snap.fields);
  const root = kind === 'page' ? norm(snap.root) : {};
  const extra = kind === 'page' ? norm(snap.extra) : {};
  const wasPublic = {
    ...extra,
    ...root,
    ...fields
  };
  const wasHidden = norm(snap.hidden);
  const set = {};
  const clear = [];
  const hidden = {};
  const kept = new Set();
  for (const entry of next || []) {
    const key = String(entry?.key || '').trim();
    const value = String(entry?.value || '').trim();
    if (!key) continue;
    kept.add(key);
    if (!value) continue; // an emptied answer is a removed answer
    if (entry.visibility === 'hidden') {
      hidden[key] = value;
      if (wasPublic[key]) clear.push(pathOf(key, kind));
    } else if (wasPublic[key] !== value) {
      set[pathOf(key, kind)] = value;
    }
  }

  /* answers the owner removed entirely, and answers left behind by a key that
     is no longer offered at all */
  for (const key of Object.keys(wasPublic)) {
    if (!kept.has(key) || !next.some(e => e.key === key && e.visibility === 'public' && String(e.value || '').trim())) {
      if (!clear.includes(pathOf(key, kind))) clear.push(pathOf(key, kind));
    }
  }
  const hiddenChanged = JSON.stringify(hidden) !== JSON.stringify(wasHidden);
  return {
    set,
    clear,
    hidden,
    empty: !Object.keys(set).length && !clear.length && !hiddenChanged
  };
}

/* ═══ WHERE THE OWNER-ONLY DOCUMENT LIVES ══════════════════════════════════

   🟥 NOT UNDER THE PROFILE, AND THE REASON IS THE CATCH-ALL. The obvious home
      was `users/{uid}/private/profile`, because the rules already grant a user
      everything under their own document. But the ruleset also ends with

          match /{col}/{document=**} { allow read, write: if signedIn() && …; }

      and `{document=**}` swallows subcollections. Firestore combines rules with
      OR, so that one line would have granted every signed-in account read on
      the very document whose whole purpose is being unreadable. A stricter rule
      cannot take back what a broader one gives; there is no deny.

   🟢 SO THE HIDDEN ANSWERS LIVE IN THEIR OWN COLLECTIONS, added to the
      ruleset's `isProtected` list — which is exactly the mechanism that file
      documents for this: "Adding a collection here is what protects it." Two
      collections rather than one, because the two have different owners and the
      rule for each should be able to say so in one line:

          profileVault/{uid}     → that uid, and nobody else
          pageVault/{pageId}     → whoever owns that page

   🟥 AND THE DOCUMENT ID IS THE OWNER, NOT A FIELD ON IT. An `ownerUid` field
      that the writer supplies is a field the writer chooses: anybody could
      create the vault for somebody else's page, name themselves the owner, and
      the real owner would find their own hidden answers unwritable. The page's
      ownership is read from the page.                                          */
export const USER_VAULT = 'profileVault';
export const PAGE_VAULT = 'pageVault';

/** The collection a profile of this kind keeps its hidden answers in. */
export const vaultCollection = kind => kind === 'page' ? PAGE_VAULT : USER_VAULT;

/** `profileVault/abc` — the same two segments on both clients. */
export function hiddenPath(kind, id) {
  return [vaultCollection(kind), String(id || '')];
}
